<template>
  <Teleport to="body">
    <div v-if="modelValue" class="rg-overlay" @click="tryClose">
      <div class="rg-dialog" @click.stop>
        <div class="rg-header">
          <h3>{{ $t('seedance.redgifs.title') }}</h3>
          <button class="rg-close" type="button" :disabled="importing" @click="tryClose">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- redgifs.com + 复制按钮：不外链跳转，让用户复制后自行打开 -->
        <div class="rg-domain">
          <span class="rg-domain-text">redgifs.com</span>
          <button class="rg-copy" type="button" @click="copyDomain">
            <svg v-if="copied" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
            {{ $t('seedance.redgifs.copy') }}
          </button>
        </div>

        <ol class="rg-steps">
          <li>{{ $t('seedance.redgifs.step1') }}</li>
          <li>{{ $t('seedance.redgifs.step2') }}</li>
          <li>{{ $t('seedance.redgifs.step3') }}</li>
        </ol>

        <input
          v-model="value"
          class="rg-input"
          type="text"
          :placeholder="$t('seedance.redgifs.placeholder')"
          :disabled="importing"
          @keydown.enter="submit"
        />
        <p class="rg-example">{{ $t('seedance.redgifs.example') }}</p>
        <p v-if="error" class="rg-error">{{ error }}</p>

        <div class="rg-actions">
          <button class="rg-btn ghost" type="button" :disabled="importing" @click="tryClose">
            {{ $t('seedance.redgifs.cancel') }}
          </button>
          <button class="rg-btn primary" type="button" :disabled="!valid || importing" @click="submit">
            <span v-if="importing" class="rg-spin" />
            {{ importing ? $t('seedance.redgifs.importing') : $t('seedance.redgifs.import') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useSnackbarStore } from '~/composables/useSnackBarStore';

const props = defineProps<{
  modelValue: boolean;
  /** 把输入交给父组件导入（父组件负责调后端 + 走上传/裁剪流程）；抛错则展示到弹窗内 */
  importFn: (input: string) => Promise<void>;
}>();
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void }>();

const { t } = useI18n();
const value = ref('');
const importing = ref(false);
const copied = ref(false);
const error = ref<string | null>(null);

/** 与后端 extractRedgifsId 保持一致的前端预校验 */
const valid = computed(() => {
  const s = value.value.trim();
  if (!s) return false;
  if (/redgifs\.com\/(?:watch|ifr)\/[A-Za-z0-9]+/i.test(s)) return true;
  return /^[A-Za-z0-9]+$/.test(s);
});

const copyDomain = async () => {
  try {
    await navigator.clipboard.writeText('https://www.redgifs.com');
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 1500);
  } catch {
    useSnackbarStore().showError(t('seedance.redgifs.copyFailed'));
  }
};

const submit = async () => {
  const input = value.value.trim();
  if (!input || importing.value) return;
  if (!valid.value) { error.value = t('seedance.redgifs.errInvalid'); return; }
  error.value = null;
  importing.value = true;
  try {
    await props.importFn(input);
    value.value = '';
    emit('update:modelValue', false);
  } catch (e: any) {
    error.value = e?.message || t('seedance.redgifs.errFailed');
  } finally {
    importing.value = false;
  }
};

const tryClose = () => {
  if (importing.value) return; // 导入中不允许关闭
  value.value = '';
  error.value = null;
  emit('update:modelValue', false);
};
</script>

<style scoped>
.rg-overlay {
  position: fixed; inset: 0; z-index: 120;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.rg-dialog {
  width: 100%; max-width: 28rem;
  background: #131313;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.25rem;
  color: white;
}
.rg-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.85rem; }
.rg-header h3 { margin: 0; font-size: 1rem; font-weight: 700; }
.rg-close { background: transparent; border: none; color: rgba(255, 255, 255, 0.5); cursor: pointer; display: flex; padding: 0.25rem; }
.rg-close:hover:not(:disabled) { color: white; }
.rg-close:disabled { cursor: not-allowed; opacity: 0.5; }
.rg-domain {
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.6rem;
  background: rgba(255, 255, 255, 0.04);
  margin-bottom: 0.85rem;
}
.rg-domain-text { flex: 1; font-family: ui-monospace, monospace; font-size: 0.85rem; user-select: all; }
.rg-copy {
  display: inline-flex; align-items: center; gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.45rem;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.75rem; cursor: pointer;
  transition: background 0.15s;
}
.rg-copy:hover { background: rgba(255, 255, 255, 0.12); color: white; }
.rg-steps {
  margin: 0 0 0.85rem; padding-left: 1.2rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem; line-height: 1.7;
}
.rg-input {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 0.85rem;
  outline: none;
}
.rg-input:focus { border-color: #d946ef; }
.rg-input:disabled { opacity: 0.5; cursor: not-allowed; }
.rg-example { margin: 0.4rem 0 0; font-size: 0.7rem; color: rgba(255, 255, 255, 0.4); }
.rg-error { margin: 0.4rem 0 0; font-size: 0.72rem; color: #f87171; }
.rg-actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.1rem; }
.rg-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.5rem 1rem;
  border: none; border-radius: 0.5rem;
  font-size: 0.85rem; font-weight: 500; cursor: pointer;
}
.rg-btn.ghost { background: transparent; color: rgba(255, 255, 255, 0.75); border: 1px solid rgba(255, 255, 255, 0.12); }
.rg-btn.ghost:hover:not(:disabled) { background: rgba(255, 255, 255, 0.05); }
.rg-btn.primary { background: linear-gradient(90deg, #d946ef, #f43f5e); color: white; }
.rg-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.rg-spin {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: white;
  animation: rg-spin 1s linear infinite;
  display: inline-block;
}
@keyframes rg-spin { to { transform: rotate(360deg); } }
</style>
